import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import ReactApexChart from 'react-apexcharts'
import { useIntl } from 'react-intl'
import { getChartDataForDay } from './core/_requests'
import moment from 'moment'
import { resolveLoraChartHeight, useViewport, useChartTouchHandlers } from './chartViewportUtils'

export type LoraDataPoint = {
  x: number
  y: number
}

export type LoraChartStats = {
  current?: number
  average?: number
  min?: number
  max?: number
}

export type LoraChartHandle = {
  navigateDay: (direction: 'forward' | 'backward') => Promise<void>
  reload: () => Promise<void>
  getCurrentDay: () => string
}

export type LoraChartProps = {
  deviceId: string
  startDate?: Date
  endDate?: Date
  isSingleDay: boolean
  initHHmm?: string
  endHHmm?: string
  onStats?: (stats: LoraChartStats) => void
  onWindowData?: (points: LoraDataPoint[]) => void
  onCurrentDayChange?: (dayYmd: string) => void
  onDataChange?: (points: LoraDataPoint[]) => void
  onError?: (message: string | null) => void
  onLoadingChange?: (loading: boolean) => void
  liveMode?: boolean
  refreshMs?: number
  isFullscreen?: boolean
  onFullscreenChange?: (value: boolean) => void
  showOverlayNav?: boolean
  onExportCsv?: () => void
}

const LoraChart = forwardRef<LoraChartHandle, LoraChartProps>(function LoraChart(
  {
    deviceId,
    startDate,
    endDate,
    isSingleDay,
    initHHmm,
    endHHmm,
    onStats,
    onWindowData,
    onCurrentDayChange,
    onDataChange,
    onError,
    onLoadingChange,
    liveMode = false,
    refreshMs = 60000,
    isFullscreen = false,
    onFullscreenChange,
    showOverlayNav = true,
    onExportCsv,
  },
  ref
) {
  const intl = useIntl()
  const viewport = useViewport()
  const containerRef = useRef<HTMLDivElement>(null)

  const [data, setData] = useState<LoraDataPoint[]>([])
  const [currentDayStr, setCurrentDayStr] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const loadedStartDayRef = useRef<string | null>(null)
  const loadedEndDayRef = useRef<string | null>(null)
  const currentDayRef = useRef<string>('')

  const chartHeight = resolveLoraChartHeight(viewport, isFullscreen)

  const { xMin, xMax } = useMemo(() => {
    const base = currentDayStr
      ? moment(currentDayStr, 'YYYY-MM-DD')
      : startDate
      ? moment(startDate)
      : moment()
    const xMinVal = base.clone().startOf('day').add(1, 'minute').valueOf()
    const xMaxVal = base.clone().add(1, 'day').startOf('day').valueOf()
    return { xMin: xMinVal, xMax: xMaxVal }
  }, [currentDayStr, startDate])

  useChartTouchHandlers({
    chartId: 'lora-time-series',
    xMin,
    xMax,
    isMobile: viewport.isMobile,
    containerRef,
    onZoomed: (min, max) => {
      if (data.length === 0) return
      const leftEdge = data[0]?.x
      const rightEdge = data[data.length - 1]?.x
      const buffer = (rightEdge - leftEdge) * 0.1

      if (min <= leftEdge + buffer) {
        loadMore('backward')
      }
      if (max >= rightEdge - buffer) {
        loadMore('forward')
      }
    },
  })

  const setLoading = useCallback(
    (loading: boolean) => {
      setIsLoading(loading)
      onLoadingChange?.(loading)
    },
    [onLoadingChange]
  )

  const updateCurrentDay = useCallback(
    (dayYmd: string) => {
      currentDayRef.current = dayYmd
      setCurrentDayStr(dayYmd)
      onCurrentDayChange?.(dayYmd)
    },
    [onCurrentDayChange]
  )

  const resetWindow = () => {
    setData([])
    loadedStartDayRef.current = null
    loadedEndDayRef.current = null
    currentDayRef.current = ''
  }

  const toPoints = (dateYmd: string, items: unknown): LoraDataPoint[] => {
    if (!Array.isArray(items)) return []
    const minuteToValue = new Map<number, number>()
    for (const it of items) {
      const raw = typeof it?.time === 'string' ? it.time : ''
      const t = raw.length > 5 ? raw.substring(0, 8) : raw
      const m = moment(`${dateYmd} ${t}`, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm'], true)
      if (!m.isValid()) continue
      const x = m.valueOf()
      const y = Number(it.value)
      if (!Number.isFinite(y)) continue
      minuteToValue.set(x, y)
    }
    return Array.from(minuteToValue.entries())
      .map(([x, y]) => ({ x, y }))
      .sort((a, b) => a.x - b.x)
  }

  const loadDay = useCallback(
    async (dateYmd: string, direction: 'forward' | 'backward') => {
      if (!deviceId) return
      setLoading(true)
      try {
        const res = await getChartDataForDay({
          deviceId,
          dateYmd,
          initTimeHHmm: isSingleDay ? initHHmm ?? '00:00' : '00:00',
          endTimeHHmm: isSingleDay ? endHHmm ?? '23:59' : '23:59',
        })
        updateCurrentDay(dateYmd)
        const points = toPoints(dateYmd, res)
        setData(points)
        loadedStartDayRef.current = dateYmd
        loadedEndDayRef.current = dateYmd
        onError?.(null)
      } catch (error) {
        console.error('Lora chart fetch error:', error)
        onError?.(
          intl.formatMessage({ id: 'LORA.FETCH_ERROR', defaultMessage: 'Failed to load data.' })
        )
      } finally {
        setLoading(false)
      }
    },
    [deviceId, isSingleDay, initHHmm, endHHmm, setLoading, updateCurrentDay, onError, intl]
  )

  const loadInitial = useCallback(async () => {
    if (!startDate || !endDate || !deviceId) return
    const startYmd = moment(startDate).format('YYYY-MM-DD')
    await loadDay(startYmd, 'forward')
  }, [startDate, endDate, deviceId, loadDay])

  const loadMore = useCallback(
    async (direction: 'forward' | 'backward') => {
      if (!startDate || !endDate) return
      const base = currentDayRef.current || moment(startDate).format('YYYY-MM-DD')
      const target =
        direction === 'backward'
          ? moment(base, 'YYYY-MM-DD').subtract(1, 'day').format('YYYY-MM-DD')
          : moment(base, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD')
      await loadDay(target, direction)
    },
    [startDate, endDate, loadDay]
  )

  const navigateDay = useCallback(
    async (direction: 'forward' | 'backward') => {
      const base =
        currentDayRef.current ||
        (startDate ? moment(startDate).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'))
      const target =
        direction === 'forward'
          ? moment(base, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD')
          : moment(base, 'YYYY-MM-DD').subtract(1, 'day').format('YYYY-MM-DD')
      await loadDay(target, direction)
    },
    [startDate, loadDay]
  )

  useImperativeHandle(
    ref,
    () => ({
      navigateDay,
      reload: loadInitial,
      getCurrentDay: () => currentDayRef.current,
    }),
    [navigateDay, loadInitial]
  )

  useEffect(() => {
    resetWindow()
    loadInitial()
  }, [
    deviceId,
    startDate?.getTime(),
    endDate?.getTime(),
    isSingleDay,
    initHHmm,
    endHHmm,
    loadInitial,
  ])

  useEffect(() => {
    if (!data || data.length === 0) {
      onStats?.({ current: undefined, average: undefined, min: undefined, max: undefined })
      onWindowData?.([])
      onDataChange?.([])
      return
    }
    const ys = data.map((d) => d.y)
    const current = ys[ys.length - 1]
    const sum = ys.reduce((a, b) => a + b, 0)
    const average = ys.length ? sum / ys.length : undefined
    const min = Math.min(...ys)
    const max = Math.max(...ys)
    onStats?.({ current, average, min, max })
    onDataChange?.(data)

    const lastPoints = data.slice(Math.max(0, data.length - 60))
    onWindowData?.(lastPoints)
  }, [data, onStats, onWindowData, onDataChange])

  useEffect(() => {
    if (!liveMode) return
    if (!endDate || !startDate) return
    const now = moment()
    const startOk = moment(startDate).isSameOrBefore(now, 'day')
    const endOk = moment(endDate).isSameOrAfter(now, 'day')
    if (!startOk || !endOk) return

    const interval = setInterval(() => {
      const today = moment().format('YYYY-MM-DD')
      loadDay(today, 'forward')
    }, Math.max(3000, refreshMs || 60000))

    return () => clearInterval(interval)
  }, [liveMode, refreshMs, deviceId, startDate?.getTime(), endDate?.getTime(), loadDay])

  const enterFullscreen = useCallback(async () => {
    onFullscreenChange?.(true)
    try {
      const el = containerRef.current
      if (el?.requestFullscreen) {
        await el.requestFullscreen()
      }
    } catch {
      // overlay fallback still works without native fullscreen
    }
  }, [onFullscreenChange])

  const exitFullscreen = useCallback(async () => {
    onFullscreenChange?.(false)
    if (document.fullscreenElement && document.exitFullscreen) {
      await document.exitFullscreen()
    }
  }, [onFullscreenChange])

  useEffect(() => {
    const onFullscreenChangeEvent = () => {
      if (!document.fullscreenElement) {
        onFullscreenChange?.(false)
      }
    }
    document.addEventListener('fullscreenchange', onFullscreenChangeEvent)
    return () => document.removeEventListener('fullscreenchange', onFullscreenChangeEvent)
  }, [onFullscreenChange])

  useEffect(() => {
    if (!isFullscreen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exitFullscreen()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isFullscreen, exitFullscreen])

  const series = useMemo(() => [{ name: 'Data', data }], [data])

  const dayOverlay = useMemo(() => {
    return currentDayStr ? moment(currentDayStr, 'YYYY-MM-DD').format('D MMM YYYY') : ''
  }, [currentDayStr])

  const chartOptions = useMemo(() => {
    return {
      chart: {
        id: 'lora-time-series',
        zoom: {
          enabled: true,
          type: 'x' as const,
          autoScaleYaxis: true,
          allowMouseWheelZoom: !viewport.isMobile,
        },
        pan: { enabled: true },
        selection: { enabled: false },
        toolbar: {
          show: true,
          offsetY: viewport.isMobile ? -4 : 0,
          tools: {
            download: true,
            selection: false,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
          autoSelected: 'zoom' as const,
          customIcons: onExportCsv
            ? [
                {
                  icon: '<i class="fa fa-file-csv" style="font-size:14px"></i>',
                  index: 7,
                  title: 'Export CSV',
                  class: 'apexcharts-export-csv-icon',
                  click: () => onExportCsv(),
                },
              ]
            : [],
        },
        animations: { enabled: false, dynamicAnimation: { enabled: false } },
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
        events: {
          zoomed: (_chartCtx: unknown, opts: { xaxis?: { min?: number; max?: number } }) => {
            const min = opts?.xaxis?.min
            const max = opts?.xaxis?.max
            if (min == null || max == null || data.length === 0) return

            const leftEdge = data[0]?.x
            const rightEdge = data[data.length - 1]?.x
            const buffer = (rightEdge - leftEdge) * 0.1

            if (min <= leftEdge + buffer) {
              loadMore('backward')
            }
            if (max >= rightEdge - buffer) {
              loadMore('forward')
            }
          },
        },
      },
      title: {
        text: dayOverlay,
        align: 'center' as const,
        margin: 10,
        offsetY: 0,
        style: {
          fontSize: viewport.isMobile ? '16px' : '20px',
          fontWeight: '700',
          color: 'rgba(100, 116, 139, 0.35)',
        },
      },
      yaxis: {
        title: {
          text: intl.formatMessage({ id: 'LORA.YAXIS_TITLE', defaultMessage: 'Value' }),
          style: { fontSize: '12px', fontWeight: 600, color: '#64748b' },
        },
      },
      xaxis: {
        type: 'datetime' as const,
        min: xMin,
        max: xMax,
        tickAmount: viewport.isMobile ? 6 : 12,
        labels: {
          style: { fontSize: viewport.isMobile ? '10px' : '12px' },
          datetimeUTC: false,
          showDuplicates: false,
          formatter: (_val: string, ts?: number) => moment(ts ?? _val).format('HH:mm'),
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        followTouchMove: true,
        x: {
          formatter: (val: number) => moment(val).format('D MMM YYYY, HH:mm'),
        },
      },
      stroke: {
        curve: 'smooth' as const,
        width: viewport.isMobile ? 2 : 2.5,
        dashArray: 0,
      },
      grid: {
        padding: {
          left: viewport.isMobile ? 4 : 12,
          right: viewport.isMobile ? 8 : 16,
          top: 8,
          bottom: 0,
        },
      },
      dataLabels: { enabled: false },
      markers: {
        size: data.length > 100 || viewport.isMobile ? 0 : 2.5,
        strokeWidth: 0,
        hover: { sizeOffset: viewport.isMobile ? 4 : 2 },
      },
      noData: { text: intl.formatMessage({ id: 'NODATA' }) },
    }
  }, [data, dayOverlay, xMin, xMax, viewport.isMobile, intl, loadMore, onExportCsv])

  const navButtonStyle = {
    minWidth: 44,
    minHeight: 44,
    opacity: 0.9,
  }

  const chartBody = (
    <>
      {viewport.isMobile && (
        <style>{`
          .lora-chart-container .apexcharts-toolbar,
          .lora-chart-fullscreen .apexcharts-toolbar {
            gap: 2px;
          }
          .lora-chart-container .apexcharts-toolbar > div,
          .lora-chart-fullscreen .apexcharts-toolbar > div {
            min-width: 28px;
            min-height: 28px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .lora-chart-container .apexcharts-toolbar svg,
          .lora-chart-fullscreen .apexcharts-toolbar svg {
            width: 18px;
            height: 18px;
          }
        `}</style>
      )}
      <div className='d-flex align-items-center justify-content-between mb-2 px-1'>
        {viewport.isMobile && !isFullscreen ? (
          <span className='fs-8 text-gray-500'>
            {intl.formatMessage({ id: 'TAGAWATT.CHART_MOBILE_HINT' })}
          </span>
        ) : (
          <span />
        )}
        {onFullscreenChange ? (
          <button
            type='button'
            className={`btn btn-sm btn-light-primary ${viewport.isMobile ? 'btn-icon' : ''}`}
            style={viewport.isMobile ? { minWidth: 36, minHeight: 36 } : undefined}
            onClick={isFullscreen ? exitFullscreen : enterFullscreen}
            aria-label={intl.formatMessage({
              id: isFullscreen ? 'TAGAWATT.EXIT_FULLSCREEN' : 'TAGAWATT.ENTER_FULLSCREEN',
            })}
          >
            <i
              className={`fa ${isFullscreen ? 'fa-compress' : 'fa-expand'}${
                viewport.isMobile ? '' : ' me-1'
              }`}
            />
            {!viewport.isMobile &&
              intl.formatMessage({
                id: isFullscreen ? 'TAGAWATT.EXIT_FULLSCREEN' : 'TAGAWATT.ENTER_FULLSCREEN',
              })}
          </button>
        ) : null}
      </div>

      <div className='w-100 position-relative' style={{ minHeight: chartHeight }}>
        <ReactApexChart
          options={chartOptions as any}
          series={series}
          type='line'
          key={`${currentDayStr}-${isSingleDay ? `${initHHmm}-${endHHmm}` : 'full'}`}
          height={chartHeight}
          width='100%'
        />

        {isLoading ? (
          <div
            className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
            style={{ background: 'rgba(255,255,255,0.4)', zIndex: 2 }}
          >
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>
                {intl.formatMessage({ id: 'COMMON.LOADING' })}
              </span>
            </div>
          </div>
        ) : null}

        {showOverlayNav ? (
          <>
            <button
              type='button'
              className='btn btn-icon btn-bg-light-primary shadow-sm rounded-circle position-absolute'
              style={{ left: 12, top: '50%', transform: 'translateY(-50%)', ...navButtonStyle }}
              onClick={() => navigateDay('backward')}
              disabled={isLoading}
              aria-label={intl.formatMessage({ id: 'LORA.LOAD_PREV' })}
            >
              <i className='fa fa-chevron-left'></i>
            </button>
            <button
              type='button'
              className='btn btn-icon btn-bg-light-primary shadow-sm rounded-circle position-absolute'
              style={{ right: 12, top: '50%', transform: 'translateY(-50%)', ...navButtonStyle }}
              onClick={() => navigateDay('forward')}
              disabled={isLoading}
              aria-label={intl.formatMessage({ id: 'LORA.LOAD_NEXT' })}
            >
              <i className='fa fa-chevron-right'></i>
            </button>
          </>
        ) : null}
      </div>
    </>
  )

  if (isFullscreen) {
    return (
      <div
        ref={containerRef}
        className='lora-chart-fullscreen'
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 2000,
          background: '#fff',
          padding: viewport.isMobile ? '12px 8px' : '20px 24px',
          overflow: 'auto',
          touchAction: 'pan-y',
        }}
      >
        {chartBody}
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className='w-100 position-relative lora-chart-container'
      style={{ minHeight: chartHeight, touchAction: 'pan-y' }}
    >
      {chartBody}
    </div>
  )
})

export default LoraChart
